import { PatientDocumentDto, UpsertPatientDocumentDto } from 'ts-types/api.types';
import axios, { AxiosError, CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from "react-message-source";
import {
  deletePatientDocument,
  findPatientDocumentById,
  getAllPatientDocuments, initialPatientDocument
} from 'services/patientDocumentServices';
import { noop } from 'utils/functionUtils';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { Dimmer, Grid, Loader, Modal } from 'semantic-ui-react';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import EditActionButton from 'components/EditActionButton';
import DeleteActionButton from 'components/DeleteActionButton';
import UpsertPatientDocumentForm from 'components/settings/patient-document/UpsertPatientDocumentForm';
import AddButton from 'components/AddButton';

interface Props extends MessageSourceProps {

}

interface State {
  hasError: boolean;
  errorMessage?: string;
  patientDocumentsLoaded: boolean;
  patientDocument?: UpsertPatientDocumentDto,
  patientDocumentId?: number,
  addEditModalOpen: boolean,
  foundPatientDocuments: Array<PatientDocumentDto>;
  cancelTokenSource: CancelTokenSource;
}

class PatientDocumentView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      hasError: false,
      patientDocumentsLoaded: false,
      foundPatientDocuments: [],
      addEditModalOpen: false,
      cancelTokenSource
    }
  }

  componentDidMount() {
    this.mounted = true;

    getAllPatientDocuments(this.state.cancelTokenSource)
        .then(foundPatientDocuments => this.setState({foundPatientDocuments}))
        .catch(error => {
          this.setState({hasError: true});
          this.handleError(error);
        })
        .finally(() => this.setState({patientDocumentsLoaded: true}));
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  handleError = (error: AxiosError) => {
    const {getMessage} = this.props;
    this.setState({errorMessage: getMessage(error.message)});
  }

  onAddPatientDocument = async () => {
    const {cancelTokenSource} = this.state;

    const upsertDto = await initialPatientDocument(cancelTokenSource);

    this.showModal(undefined,upsertDto);
  }

  openAddEditModal = (id: number | undefined) => {
    const {cancelTokenSource} = this.state;

    if (id) {
      findPatientDocumentById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);
    }
  };

  showModal = (id?: number, patientDocument?: UpsertPatientDocumentDto) => {

    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        patientDocument: patientDocument,
        patientDocumentId: id
      }
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      addEditModalOpen: false,
      patientDocument: undefined,
      patientDocumentId: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        patientDocumentsLoaded: true
      })
    };

    getAllPatientDocuments(cancelTokenSource)
        .then(response => {
          this.setState({
            foundPatientDocuments: response
          });
        })
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  };

  deletePatientDocument = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deletePatientDocument(id, cancelTokenSource)
        .then(response => {
          this.setState(({foundPatientDocuments}: State) => ({
            foundPatientDocuments: foundPatientDocuments
                .filter((patientDocument: PatientDocumentDto) => patientDocument.id !== id)
          }))
        }).catch(noop);
  };

  render(): React.ReactNode {
    const {patientDocumentsLoaded} = this.state;
    const {getMessage} = this.props;

    return (
        <PraxStyledSegment>

          <div>
            <Grid.Row>
              <Grid.Column width={3} verticalAlign="middle">
              </Grid.Column>
              <Grid.Column width={9}>
                <AddButton buttonMessage={getMessage("button.add")} onClick={this.onAddPatientDocument}/>
              </Grid.Column>
            </Grid.Row>
          </div>

          <StyledViewTableRow>
            {!patientDocumentsLoaded ? this.renderLoader() : this.renderPatientDocumentsSegment()}
          </StyledViewTableRow>

          {this.renderUpsertPatientDocumentForm()}
        </PraxStyledSegment>
    );
  }

  patientDocumentRowGetter = ({index}: any) => {
    const {foundPatientDocuments} = this.state;

    Object.assign(foundPatientDocuments[index], {index: index + 1});

    return foundPatientDocuments[index]
  };

  foundPatientDocumentsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundPatientDocuments} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(foundPatientDocuments[index].id)}
        >
          {columns}
        </div>
    );
  };

  renderPatientDocumentsSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundPatientDocuments} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundPatientDocuments.length}
            rowGetter={this.patientDocumentRowGetter}
            rowRenderer={this.foundPatientDocumentsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 200,
                label: (getMessage("patientDocument.code")),
                dataKey: "code"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("patientDocument.name")),
                dataKey: "name"
              },
              {
                width: 200,
                label: (getMessage("patientDocument.fastPrint")),
                dataKey: "fastPrint",
                cellRenderer: this.fastPrintCellRenderer
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  fastPrintCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {
      return getMessage("patientDocument.fastPrint")
    }
    return "";
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("patientDocument.confirmDelete")}
              onConfirm={() => this.deletePatientDocument(cellData)}
          />
        </React.Fragment>
    );
  };

  renderLoader = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline='centered' size='medium'>{getMessage("patientDocument.loading")}</Loader>
        </Dimmer>
    );
  };

  renderUpsertPatientDocumentForm = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, patientDocument, patientDocumentId} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("patientDocuments")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertPatientDocumentForm
                patientDocument={patientDocument}
                patientDocumentId={patientDocumentId}
                onSave={this.onSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    )
  }

}

export default withMessages(PatientDocumentView);
